import { createServerFn } from '@tanstack/react-start'
import { requireSupabaseAuth } from '@/integrations/supabase/auth-middleware'

/**
 * Sends the branded Welcome email the first time an authenticated user with a
 * confirmed email address hits the app. Idempotent: guarded by
 * `profiles.welcome_email_sent_at` AND `email_send_log.message_id` = `welcome-{userId}`.
 * Never sends unless the user's Supabase account email is confirmed.
 */
export const sendWelcomeEmailIfNeeded = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context as { userId: string }
    if (!userId) return { skipped: 'no_user' as const }

    const [{ supabaseAdmin }, { TEMPLATES }, React, { render }] = await Promise.all([
      import('@/integrations/supabase/client.server'),
      import('@/lib/email-templates/registry'),
      import('react'),
      import('@react-email/render'),
    ])

    // 1. Confirm the auth user has a confirmed email.
    const { data: userData, error: userErr } = await supabaseAdmin.auth.admin.getUserById(userId)
    if (userErr || !userData?.user) return { skipped: 'user_not_found' as const }
    const user = userData.user
    if (!user.email || !user.email_confirmed_at) {
      return { skipped: 'email_not_confirmed' as const }
    }

    // 2. Idempotency guard.
    const { data: profile } = await supabaseAdmin
      .from('profiles')
      .select('display_name, welcome_email_sent_at')
      .eq('id', userId)
      .maybeSingle()
    if (profile?.welcome_email_sent_at) return { skipped: 'already_sent' as const }

    const messageId = `welcome-${userId}`
    const { data: existingLog } = await supabaseAdmin
      .from('email_send_log')
      .select('id')
      .eq('message_id', messageId)
      .maybeSingle()
    if (existingLog) {
      await supabaseAdmin
        .from('profiles')
        .update({ welcome_email_sent_at: new Date().toISOString() })
        .eq('id', userId)
      return { skipped: 'log_exists' as const }
    }

    // 3. Suppression check.
    const recipient = user.email
    const { data: suppressed } = await supabaseAdmin
      .from('suppressed_emails')
      .select('id')
      .eq('email', recipient.toLowerCase())
      .maybeSingle()
    if (suppressed) {
      await supabaseAdmin.from('email_send_log').insert({
        message_id: messageId,
        template_name: 'welcome',
        recipient_email: recipient,
        status: 'suppressed',
      })
      return { skipped: 'suppressed' as const }
    }

    // 3b. Get or create the recipient's unsubscribe token. The email API rejects
    // transactional sends without one (400 missing_unsubscribe).
    const normalizedEmail = recipient.toLowerCase()
    let unsubscribeToken: string | null = null
    const { data: existingToken } = await supabaseAdmin
      .from('email_unsubscribe_tokens')
      .select('token, used_at')
      .eq('email', normalizedEmail)
      .maybeSingle()

    if (existingToken?.token && !existingToken.used_at) {
      unsubscribeToken = existingToken.token
    } else if (!existingToken) {
      const generated = crypto.randomUUID().replace(/-/g, '')
      await supabaseAdmin
        .from('email_unsubscribe_tokens')
        .upsert({ token: generated, email: normalizedEmail }, { onConflict: 'email', ignoreDuplicates: true })
      const { data: storedToken } = await supabaseAdmin
        .from('email_unsubscribe_tokens')
        .select('token')
        .eq('email', normalizedEmail)
        .maybeSingle()
      unsubscribeToken = storedToken?.token ?? generated
    }

    if (!unsubscribeToken) {
      // Token exists but was already used — the recipient opted out.
      await supabaseAdmin.from('email_send_log').insert({
        message_id: messageId,
        template_name: 'welcome',
        recipient_email: recipient,
        status: 'suppressed',
        error_message: 'Unsubscribe token already used',
      })
      return { skipped: 'suppressed' as const }
    }

    const tmpl = TEMPLATES['welcome']
    if (!tmpl) throw new Error('welcome template missing from registry')
    const element = React.createElement(tmpl.component, {
      displayName: profile?.display_name ?? undefined,
    })
    const html = await render(element)
    const text = await render(element, { plainText: true })
    const subject = typeof tmpl.subject === 'function' ? tmpl.subject({}) : tmpl.subject

    await supabaseAdmin.from('email_send_log').insert({
      message_id: messageId,
      template_name: 'welcome',
      recipient_email: recipient,
      status: 'pending',
    })

    const SITE_NAME = process.env.FROM_NAME ?? 'BidNova Auctions'
    const FROM_DOMAIN = process.env.FROM_DOMAIN ?? 'bidnovaauctions.uk'
    const SENDER_DOMAIN = process.env.SENDER_DOMAIN ?? `mail.${FROM_DOMAIN}`
    const FROM_EMAIL = process.env.FROM_EMAIL ?? `notifications@${FROM_DOMAIN}`
    const REPLY_TO_EMAIL = process.env.REPLY_TO ?? `support@${FROM_DOMAIN}`

    const { error: enqueueError } = await supabaseAdmin.rpc('enqueue_email', {
      queue_name: 'transactional_emails',
      payload: {
        message_id: messageId,
        to: recipient,
        from: `${SITE_NAME} <${FROM_EMAIL}>`,
        reply_to: REPLY_TO_EMAIL,
        sender_domain: SENDER_DOMAIN,
        subject,
        html,
        text,
        purpose: 'transactional',
        label: 'welcome',
        // Suffixed so recipients whose earlier send permanently failed are not
        // blocked by the provider's "already failed" idempotency record.
        idempotency_key: `${messageId}-v2`,
        unsubscribe_token: unsubscribeToken,
        queued_at: new Date().toISOString(),
      },
    })

    if (enqueueError) {
      await supabaseAdmin.from('email_send_log').insert({
        message_id: messageId,
        template_name: 'welcome',
        recipient_email: recipient,
        status: 'failed',
        error_message: enqueueError.message,
      })
      throw new Error(`Failed to enqueue welcome email: ${enqueueError.message}`)
    }

    await supabaseAdmin
      .from('profiles')
      .update({ welcome_email_sent_at: new Date().toISOString() })
      .eq('id', userId)

    return { sent: true, messageId }
  })
